import { SocialIcon } from "./SocialIcon";
import { activeSocial, type SocialAccount } from "../lib/social";

/**
 * Brand colours, used by the "cards" variant as each card's accent. X's grey
 * is deliberate — pure black vanishes against the dark theme's surfaces.
 */
const BRAND: Record<string, string> = {
  linkedin: "#0a66c2",
  youtube: "#cc0000",
  facebook: "#1877f2",
  instagram: "#c13584",
  x: "#657786",
};

/** "https://www.linkedin.com/school/…" -> "linkedin.com" — factual, no gloss. */
const host = (url: string) => new URL(url).hostname.replace(/^www\./, "");

interface SocialLinksProps {
  /**
   * "inline" — a mono rule-separated row for the footer brand column.
   * "list"   — stacked named rows.
   * "cards"  — brand-accented cards, for the /contact page.
   */
  variant?: "inline" | "list" | "cards";
  /** Heading above the links. Omit for none. */
  heading?: string;
  accounts?: SocialAccount[];
}

/**
 * The institute's social accounts, as brand icons.
 *
 * The "inline" footer row is icon-only, so each link's accessible name comes
 * entirely from its aria-label — the glyph itself is aria-hidden. The "list"
 * variant on /contact pairs the icon with its name, since that card has the
 * width for it and reads better alongside the other contact blocks.
 *
 * Renders nothing at all when no account has a URL yet, so the scaffold cannot
 * ship as a row of dead links.
 */
export function SocialLinks({ variant = "inline", heading, accounts }: SocialLinksProps) {
  const live = activeSocial(accounts);
  if (live.length === 0) return null;

  const wrapper =
    variant === "list" ? "social-list-variant" : variant === "cards" ? "social-cards" : "social-inline";

  return (
    <div className={"social " + wrapper}>
      {heading && <div className="social-heading">{heading}</div>}
      <ul className="social-list">
        {live.map((a) => (
          <li key={a.platform}>
            <a
              className="social-link"
              href={a.url}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`BIET on ${a.label} (external site)`}
              style={
                variant === "cards"
                  ? ({ ["--soc-accent" as string]: BRAND[a.platform] } as React.CSSProperties)
                  : undefined
              }
            >
              {variant === "cards" ? (
                <>
                  <span className="soc-ic" aria-hidden="true">
                    <SocialIcon platform={a.platform} />
                  </span>
                  <span className="soc-txt">
                    <span className="social-name">{a.label}</span>
                    <span className="soc-host">{host(a.url)}</span>
                  </span>
                  <span className="soc-arr" aria-hidden="true">
                    →
                  </span>
                </>
              ) : (
                <>
                  <SocialIcon platform={a.platform} />
                  {variant === "list" && (
                    <>
                      <span className="social-name">{a.label}</span>
                      <span aria-hidden="true">→</span>
                    </>
                  )}
                </>
              )}
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}
